import React, { useState, useEffect } from "react";
import { X, Hash, ArrowLeftRight, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

/**
 * Modal admin — renomeia o código canônico de uma unidade (P001, P002, …).
 *
 * Comportamento: se o novo código já pertence a outra unidade, o backend
 * faz swap atômico (a outra unidade recebe o código antigo). A UI avisa
 * a troca antes de submeter para o admin não ser pego de surpresa.
 */
export const RenameClinicCodeModal = ({ clinic, allClinics, onClose, onSaved }) => {
  const [code, setCode] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setCode((clinic?.code || "").replace(/^P0*/i, "") || "");
  }, [clinic]);

  if (!clinic) return null;
  const oldCode = clinic.code || "—";
  const normalized = (() => {
    const digits = (code || "").replace(/\D/g, "");
    if (!digits) return "";
    const n = parseInt(digits, 10);
    if (!n || n < 1 || n > 9999) return "";
    return `P${String(n).padStart(3, "0")}`;
  })();
  const collision = normalized && (allClinics || []).find(
    (c) => c.id !== clinic.id && (c.code || "").toUpperCase() === normalized,
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!normalized) {
      toast.error("Código inválido. Use P + até 4 dígitos.");
      return;
    }
    if (normalized === oldCode) {
      onClose();
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.patch(
        `/admin/clinics/${clinic.id}/code`,
        { new_code: normalized },
      );
      if (data.swapped_with) {
        toast.success(
          `Códigos trocados: ${clinic.name} → ${data.code} · ${data.swapped_with.name} → ${data.swapped_with.new_code}`,
          { duration: 6000 },
        );
      } else {
        toast.success(`Código renomeado para ${data.code}.`);
      }
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao renomear código");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="rename-clinic-code-modal"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4"
      >
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
              <Hash className="w-3.5 h-3.5" /> Renomear código
            </div>
            <h3 className="font-display font-bold text-lg text-[#0B2B62] mt-1">
              {clinic.name}
            </h3>
            <p className="text-xs text-slate-500">
              Código atual: <span className="font-mono font-bold">{oldCode}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            aria-label="Fechar"
            data-testid="rename-code-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700">Novo código</label>
          <div className="mt-1 flex items-center rounded-lg border border-slate-300 focus-within:border-[#0B2B62] focus-within:ring-1 focus-within:ring-[#0B2B62] overflow-hidden">
            <span className="px-3 py-2 bg-slate-50 text-slate-500 font-mono text-sm border-r border-slate-200">P</span>
            <input
              type="text"
              inputMode="numeric"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 4))}
              placeholder="001"
              className="flex-1 px-3 py-2 font-mono text-sm outline-none"
              data-testid="rename-code-input"
            />
            {normalized && (
              <span className="px-3 py-2 text-xs text-emerald-700 bg-emerald-50 font-mono">
                → {normalized}
              </span>
            )}
          </div>
          <p className="text-[11px] text-slate-500 mt-1.5">
            Formato P + 3 dígitos (P001 – P9999). Sequência preservada — se você trocar por um código já existente, o sistema faz swap automático.
          </p>
        </div>

        {collision && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-900 flex items-start gap-2" data-testid="rename-code-swap-warning">
            <ArrowLeftRight className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
            <div>
              <div className="font-bold">Swap automático</div>
              O código <span className="font-mono">{normalized}</span> já pertence a <b>{collision.name}</b>. Essa unidade vai receber o código antigo <span className="font-mono">{oldCode}</span> em troca.
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-slate-600 hover:text-slate-800 px-3 py-2 rounded-md"
            data-testid="rename-code-cancel-btn"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving || !normalized}
            className="text-sm font-bold text-white bg-[#0B2B62] hover:bg-[#0A2456] disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 rounded-md inline-flex items-center gap-1.5"
            data-testid="rename-code-submit-btn"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {collision ? "Trocar códigos" : "Renomear"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default RenameClinicCodeModal;
